import React from 'react'
import { hydrateRoot, createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { HelmetProvider } from "react-helmet-async"
import { TooltipProvider } from "@/components/ui/tooltip"
import { Toaster } from "@/components/ui/toaster"
import { Toaster as Sonner } from "@/components/ui/sonner"
import App from './App.tsx'
import './index.css'
import { initSentry } from '@/lib/sentry'
import { installStuckWatchdog } from '@/lib/watchdog'
import { captureAppsumoLicenseFromUrl } from '@/lib/appsumoLicense'

const rootElement = document.getElementById('root')!;
initSentry();
installStuckWatchdog();
// Capture an AppSumo license from the landing URL BEFORE React renders or any
// auth redirect strips the query param. Stored in localStorage; while stashed,
// AuthProvider's safety net snaps navigation to /appsumo/welcome, the page
// that owns the whole claim flow. This must live HERE: index.html loads
// entry-client.tsx, main.tsx is not the production entry.
captureAppsumoLicenseFromUrl(window.location.search);
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Keep data in cache for 5 minutes
      staleTime: 5 * 60 * 1000,
      // Keep unused data in cache for 10 minutes
      gcTime: 10 * 60 * 1000,
      // Don't refetch on window focus by default (prevents unwanted refreshes)
      refetchOnWindowFocus: false,
      // Retry failed requests once
      retry: 1,
    },
  },
})

// Check if this is a server-rendered page
// SSR pages can have window.__SSR_DATA__ (for microsites with data fetching)
// OR just have server-rendered content (for public routes without data)
const hasSSRData = typeof window !== 'undefined' && '__SSR_DATA__' in window;
const hasServerContent = rootElement.innerHTML.trim().length > 0;

// Force CSR for localhost (local development)
// Only use SSR for production/Vercel deployments
const isLocalhost = window.location.hostname === 'localhost' ||
                    window.location.hostname === '127.0.0.1' ||
                    window.location.hostname.includes('192.168.');

const isSSRPage = !isLocalhost && hasServerContent; // Only use SSR in production

const MAIN_DOMAINS = ['fly-social.com', 'www.fly-social.com'];
const isVercelPreview = window.location.hostname.endsWith('.vercel.app');
const isAgencyDomain = !isLocalhost && !isVercelPreview && !MAIN_DOMAINS.includes(window.location.hostname);

// Microsites always have __SSR_DATA__ injected by the server.
// Agency dashboards use CSR (no SSR data). So we can distinguish:
const isAgencyMicrosite = isAgencyDomain && hasSSRData;
const isAgencyDashboard = isAgencyDomain && !hasSSRData;

console.log('[entry-client] Initializing...', {
  hasSSRData,
  hasServerContent,
  isLocalhost,
  isVercelPreview,
  isSSRPage,
  hostname: window.location.hostname,
  isAgencyDomain,
  isAgencyMicrosite,
  isAgencyDashboard,
  rootContentLength: rootElement.innerHTML.length
});

if (isAgencyMicrosite) {
  console.log('[entry-client] Agency microsite detected:', window.location.hostname);
} else if (isAgencyDashboard) {
  console.log('[entry-client] Agency dashboard detected:', window.location.hostname);
} else if (isVercelPreview) {
  console.log('[entry-client] Vercel preview deployment');
} else {
  console.log('[entry-client] Main domain (fly-social.com)');
}

const AppWithProviders = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

if (isSSRPage) {
  // SSR: Hydrate server-rendered HTML
  // Used for:
  // 1. Microsite subdomains (landing pages, store locators) - with SSR data
  // 2. Public routes on main domain (marketing, tools, blogs) - without SSR data
  console.log('[entry-client] Using SSR hydration', hasSSRData ? '(with SSR data)' : '(without SSR data)');
  hydrateRoot(
    rootElement,
    <AppWithProviders />
  );
} else {
  // CSR: Client-side render from scratch
  // Used for authenticated routes (/fly-app/*) where server returns empty HTML shell
  console.log('[entry-client] Using CSR (client-side rendering)');
  createRoot(rootElement).render(
    <AppWithProviders />
  );
}
